/**
 * `growth/gate` — ad eligibility. A **PURE** function (no SDK, no clock,
 * no storage): it receives state and returns `{ allow, reason }`.
 *
 * Implements the COMPLETE frequency-capping table from the ads strategy:
 *
 * | Rule                                    | Default |
 * |-----------------------------------------|---------|
 * | Interstitial per session                | 1, and only with ≥1 value unit |
 * | Interstitial per day                   | 3 |
 * | Minimum gap between interstitials      | ≥10 min |
 * | First interstitial ever                | never in the 1st session |
 * | Native per screen                      | 1 |
 * | Visible banner                         | 1 |
 * | Rewarded                                | 3 rewards/day PER TYPE |
 * | Ignored rewarded offer                  | 2/day, with backoff to 1 after 3 days |
 * | Global interruption budget              | ≤1 interstitial / 10 active min |
 * | Premium                                 | 0 ads, immediate |
 *
 * The `reason` is a stable code (not UI text): it feeds telemetry and is what
 * the tests assert.
 *
 * DAILY counters arrive already normalized by `AdCapsStore` (which resets
 * when the day changes): the gate deliberately knows no time zones.
 */

import type { AdCapPolicy, AdGateDecision, AdGateInput, AdGateReason, AdKind } from "./types";

const MINUTE_MS = 60_000;

/** Portfolio defaults (§4 of the strategy). Config-over-code: each app
 *  can override them by placement without touching this function. */
export const DEFAULT_AD_CAP_POLICY: AdCapPolicy = {
  interstitialPerSession: 1,
  interstitialPerDay: 3,
  minGapBetweenInterstitialsMs: 10 * MINUTE_MS,
  allowInterstitialOnFirstSession: false,
  minUnitsCompleted: 1,
  rewardsPerDayPerType: 3,
  offersPerDayPerPlacement: 2,
  ignoredDaysBeforeBackoff: 3,
  offersPerDayWhenBackedOff: 1,
  nativePerScreen: 1,
  bannersVisible: 1,
  interruptionBudgetMs: 10 * MINUTE_MS,
};

/**
 * The four caps the back office can move at runtime (`adcaps.*` from
 * CONFIG_OVER_CODE, `GET /api/config`). It is declared structurally so the
 * kit remains API-package-AGNOSTIC: what arrives is the `AdCapsSettings`
 * DTO, but `growth/` does not need to know that.
 */
export interface AdCapsSettingsLike {
  interstitialPerSession: number;
  interstitialPerDay: number;
  rewardsPerDayPerType: number;
  offersPerDayPerPlacement: number;
}

/**
 * Translates the `adcaps.*` values resolved by the SERVER into the gate policy.
 *
 * True config-over-code: without this, values traveled in the DTO, were
 * audited and edited in the panel… while the client kept using
 * `DEFAULT_AD_CAP_POLICY`. It deliberately returns a `Partial` — it overrides
 * only the four configurable keys and leaves the others intact (including the
 * child regime, which is NOT configurable).
 *
 * With no config (offline guest, server down) it returns `{}`: the code default
 * wins, which is the historical behavior. A non-finite or negative value is
 * ignored rather than bringing down the capping: the server already clamps it
 * (`resolve::clamp`); this is the second safety net in case a corrupt DTO
 * arrives.
 */
export function adCapPolicyFromSettings(
  settings: AdCapsSettingsLike | undefined | null,
): Partial<AdCapPolicy> {
  if (!settings) {
    return {};
  }
  const policy: Partial<AdCapPolicy> = {};
  const put = (key: keyof AdCapsSettingsLike & keyof AdCapPolicy) => {
    const value = settings[key];
    if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
      policy[key] = value;
    }
  };
  put("interstitialPerSession");
  put("interstitialPerDay");
  put("rewardsPerDayPerType");
  put("offersPerDayPerPlacement");
  return policy;
}

/**
 * Child regime (COPPA / Play Families / App Store Kids): STRICTER than the
 * general policy. Interstitials only live `between-screens` (never inside a
 * game), max 1/session and 2/day; native is not used; rewarded requires an
 * adult gate and is disabled by default.
 */
export const CHILD_AD_CAP_POLICY: AdCapPolicy = {
  ...DEFAULT_AD_CAP_POLICY,
  interstitialPerSession: 1,
  interstitialPerDay: 2,
  rewardsPerDayPerType: 1,
};

/**
 * NUMERIC caps of the policy (all except the single boolean).
 *
 * `Exclude` instead of a hand-written list: if a cap is added to
 * `AdCapPolicy` tomorrow, it enters here automatically and
 * `STRICTER_DIRECTION` stops compiling until someone declares whether
 * stricter means the minimum or the maximum.
 */
type NumericCapKey = Exclude<keyof AdCapPolicy, "allowInterstitialOnFirstSession">;

/**
 * Direction of "stricter" for each numeric cap.
 *
 * `"min"` = a lower number is less annoying (impression caps);
 * `"max"` = a higher number is less annoying, so stricter is the larger one
 * (minimum gaps, interruption budget, required value units).
 *
 * The `Record<NumericCapKey, …>` is an exhaustiveness guard: a cap cannot
 * be added without classifying its direction.
 */
const STRICTER_DIRECTION: Readonly<Record<NumericCapKey, "min" | "max">> = {
  interstitialPerSession: "min",
  interstitialPerDay: "min",
  minGapBetweenInterstitialsMs: "max",
  minUnitsCompleted: "max",
  rewardsPerDayPerType: "min",
  offersPerDayPerPlacement: "min",
  ignoredDaysBeforeBackoff: "min",
  offersPerDayWhenBackedOff: "min",
  nativePerScreen: "min",
  bannersVisible: "min",
  interruptionBudgetMs: "max",
};

/**
 * The STRICTEST of two policies, key by key.
 *
 * Same principle as `clampAdsConfigToTier` in `config.ts`: **the classification
 * beats the flag**. It exists because `input.policy` is no longer only a
 * code injection — since CONFIG_OVER_CODE it carries the `adcaps.*` values
 * an admin edits at runtime, and without this an
 * `adcaps.interstitial_per_day = 3` would LOOSEN the child cap of 2 from
 * the back office. An override may tighten the child regime; it must never
 * loosen it.
 *
 * The complete explicit object is built instead of iterating and assigning:
 * the return type itself forces every key to be covered (forgetting one
 * does not compile), and no type assertion is needed.
 */
export function strictestAdCapPolicy(a: AdCapPolicy, b: AdCapPolicy): AdCapPolicy {
  const pick = (key: NumericCapKey): number =>
    STRICTER_DIRECTION[key] === "min" ? Math.min(a[key], b[key]) : Math.max(a[key], b[key]);
  return {
    interstitialPerSession: pick("interstitialPerSession"),
    interstitialPerDay: pick("interstitialPerDay"),
    minGapBetweenInterstitialsMs: pick("minGapBetweenInterstitialsMs"),
    // The only boolean: `false` (not allowed in the 1st session) is the stricter one.
    allowInterstitialOnFirstSession:
      a.allowInterstitialOnFirstSession && b.allowInterstitialOnFirstSession,
    minUnitsCompleted: pick("minUnitsCompleted"),
    rewardsPerDayPerType: pick("rewardsPerDayPerType"),
    offersPerDayPerPlacement: pick("offersPerDayPerPlacement"),
    ignoredDaysBeforeBackoff: pick("ignoredDaysBeforeBackoff"),
    offersPerDayWhenBackedOff: pick("offersPerDayWhenBackedOff"),
    nativePerScreen: pick("nativePerScreen"),
    bannersVisible: pick("bannersVisible"),
    interruptionBudgetMs: pick("interruptionBudgetMs"),
  };
}

/** Formats allowed in the child regime (§6). */
const CHILD_ALLOWED_KINDS: ReadonlySet<AdKind> = new Set(["banner", "interstitial", "rewarded"]);

function deny(reason: AdGateReason): AdGateDecision {
  return { allow: false, reason };
}

const ALLOW: AdGateDecision = { allow: true, reason: "allow" };

function interstitialDecision(i: AdGateInput, p: AdCapPolicy): AdGateDecision {
  const { caps, session } = i;

  // First-run is honored in full: native/banner can live on day 1,
  // interstitial cannot. Burning the first session hurts retention (and LTV).
  if (session.isFirstEver && !p.allowInterstitialOnFirstSession) {
    return deny("first-session");
  }
  // An interstitial is legal only AFTER completing a value unit: that is what
  // turns "task complete" into a breather rather than an interruption.
  if (session.unitsCompleted < p.minUnitsCompleted) {
    return deny("no-value-unit");
  }
  if (caps.interstitialsThisSession >= p.interstitialPerSession) {
    return deny("session-cap");
  }
  if (caps.interstitialsToday >= p.interstitialPerDay) {
    return deny("daily-cap");
  }
  if (
    caps.lastInterstitialAt !== undefined &&
    i.now - caps.lastInterstitialAt < p.minGapBetweenInterstitialsMs
  ) {
    return deny("min-gap");
  }
  // Global budget: ≤1 interruptive impression per `interruptionBudgetMs` of ACTIVE use.
  // The first one in a session is free (0 × budget ≤ activeMs); from then on,
  // each new interruption must be "earned" with real usage.
  if (caps.interstitialsThisSession * p.interruptionBudgetMs > session.activeMs) {
    return deny("interruption-budget");
  }
  return ALLOW;
}

function rewardedDecision(i: AdGateInput, p: AdCapPolicy): AdGateDecision {
  const { caps, placementId, rewardType } = i;

  // Anti-farming: the cap is per reward TYPE, not per placement — otherwise,
  // two placements granting the same thing would double the cap.
  const type = rewardType ?? placementId;
  if ((caps.rewardsToday[type] ?? 0) >= p.rewardsPerDayPerType) {
    return deny("reward-daily-cap");
  }

  // Backoff: if the user has ignored the offer for days, show it less often.
  const ignored = caps.ignoredDays[placementId] ?? 0;
  const offerLimit =
    ignored >= p.ignoredDaysBeforeBackoff
      ? p.offersPerDayWhenBackedOff
      : p.offersPerDayPerPlacement;
  const offers = caps.offersToday[placementId] ?? 0;
  if (offers >= offerLimit) {
    return deny(ignored >= p.ignoredDaysBeforeBackoff ? "offer-backoff" : "offer-daily-cap");
  }
  return ALLOW;
}

/**
 * Should this ad be shown NOW?
 *
 * The order of negative decisions is deliberate: absolute conditions first
 * (premium, task in progress, child regime), then capping. Thus the `reason`
 * sent to telemetry names the REAL cause instead of saying "cap reached"
 * when it was actually premium.
 */
export function adGate(input: AdGateInput): AdGateDecision {
  const base = input.user.child ? CHILD_AD_CAP_POLICY : DEFAULT_AD_CAP_POLICY;
  const requested: AdCapPolicy = { ...base, ...input.policy };
  // In the child regime the floor is NOT negotiable: the requested policy
  // (env, admin override, or code injection) can only TIGHTEN it.
  const policy = input.user.child
    ? strictestAdCapPolicy(CHILD_AD_CAP_POLICY, requested)
    : requested;

  // 1. Premium removes ads from the ENTIRE app immediately, without waiting
  //    for sync (the optimistic local flag wins after purchase).
  if (input.user.premium) {
    return deny("premium");
  }

  // 2. Never in the middle of a task: active input, a running timer, a
  //    half-finished transaction, or an ongoing gesture. This beats any
  //    capping schedule.
  if (input.inFlight) {
    return deny("in-flight");
  }

  // 3. Child regime.
  if (input.user.child) {
    if (!CHILD_ALLOWED_KINDS.has(input.kind)) {
      return deny("child-policy");
    }
    if (input.kind === "interstitial" && input.breakpoint !== "between-screens") {
      // Never inside a game: only the transition between screens.
      return deny("child-policy");
    }
    if (input.kind === "rewarded" && !input.user.adultGateCleared) {
      // Rewarded disabled by default: only after the parental gate.
      return deny("child-policy");
    }
  }

  switch (input.kind) {
    case "interstitial": {
      return interstitialDecision(input, policy);
    }
    case "rewarded": {
      return rewardedDecision(input, policy);
    }
    case "native": {
      return input.caps.nativesOnScreen >= policy.nativePerScreen ? deny("screen-cap") : ALLOW;
    }
    case "banner": {
      return input.caps.bannersVisible >= policy.bannersVisible ? deny("screen-cap") : ALLOW;
    }
    default: {
      return deny("child-policy");
    }
  }
}
